import { useForm } from '@tanstack/react-form';
import {
  EmailAuthProvider,
  reauthenticateWithCredential,
  signInWithEmailAndPassword,
  signOut,
} from 'firebase/auth';
import { useNavigate } from 'react-router';
import { toast } from 'sonner';
import { z } from 'zod';

import { CardContent, Label } from '@virtual-care/shadcn';
import {
  AuthLayout,
  FieldMessage,
  FormMessage,
  PasswordInput,
  TheButton,
  client,
} from '@virtual-care/ui';

import { auth } from '../firebase.tsx';

const schema = z
  .object({
    currentPassword: z.string().min(1, { message: 'Current password is required' }),
    password: z
      .string()
      .min(8, { message: 'Password must be at least 8 characters' }),
    confirmPassword: z
      .string()
      .min(8, { message: 'Password confirmation is required' }),
  })
  .refine((data) => data.password === data.confirmPassword, {
    message: 'Passwords do not match',
    path: ['confirmPassword'],
  });

export default function ChangePassword() {
  const navigate = useNavigate();

  const form = useForm({
    defaultValues: {
      currentPassword: '',
      password: '',
      confirmPassword: '',
    },
    validators: {
      onSubmit: schema,
      onSubmitAsync: async (props) => {
        const issue = (field: string, message: string) =>
          ({ [field]: [{ message }] }) as Record<
            string,
            { message: string }[]
          >;

        const user = auth.currentUser;
        if (!user || !user.email) {
          return issue('form', 'Something went wrong. Please sign in again.');
        }

        try {
          const credential = EmailAuthProvider.credential(
            user.email,
            props.value.currentPassword,
          );
          await reauthenticateWithCredential(user, credential);
        } catch {
          return issue('currentPassword', 'Current password is incorrect');
        }

        try {
          await client.request('POST /me/password', {
            newPassword: props.value.password,
          });
        } catch (error) {
          const status =
            error && typeof error === 'object' && 'status' in error
              ? (error as { status: number }).status
              : 0;
          if (status === 400) {
            return issue('password', 'Password is too weak');
          }
          if (status === 401) {
            return issue('currentPassword', 'Current password is incorrect');
          }
          return issue('form', "Couldn't complete the update. Please try again.");
        }

        try {
          const credential = await signInWithEmailAndPassword(
            auth,
            user.email,
            props.value.password,
          );
          await credential.user.getIdToken(true);
        } catch (error) {
          console.error(error);
          toast.success('Password updated. Please sign in again.');
          await signOut(auth);
          await navigate('/signin');
          return undefined;
        }

        await navigate('/');
        return undefined;
      },
    },
  });

  return (
    <AuthLayout
      center
      title="Change password"
      subtitle="For security, set a new password before continuing."
    >
      <CardContent>
        <form
          className="space-y-4"
          onSubmit={async (e) => {
            e.preventDefault();
            e.stopPropagation();
            await form.handleSubmit();
          }}
        >
          <form.Field
            name="currentPassword"
            children={(field) => (
              <div className="grid w-full items-center gap-2">
                <Label className="text-sm" htmlFor="currentPassword">
                  Current password
                </Label>
                <PasswordInput
                  id="currentPassword"
                  dir="ltr"
                  autoComplete="current-password"
                  value={field.state.value}
                  onBlur={field.handleBlur}
                  onChange={(e) => field.handleChange(e.target.value)}
                />
                <FieldMessage errors={field.state.meta.errors} />
              </div>
            )}
          />

          <form.Field
            name="password"
            children={(field) => (
              <div className="grid w-full items-center gap-2">
                <Label className="text-sm" htmlFor="password">
                  New password
                </Label>
                <PasswordInput
                  id="password"
                  dir="ltr"
                  autoComplete="new-password"
                  value={field.state.value}
                  onBlur={field.handleBlur}
                  onChange={(e) => field.handleChange(e.target.value)}
                />
                <p className="text-muted-foreground text-sm">
                  Must be at least 8 characters
                </p>
                <FieldMessage errors={field.state.meta.errors} />
              </div>
            )}
          />

          <form.Field
            name="confirmPassword"
            children={(field) => (
              <div className="grid w-full items-center gap-2">
                <Label className="text-sm" htmlFor="confirmPassword">
                  Confirm password
                </Label>
                <PasswordInput
                  id="confirmPassword"
                  dir="ltr"
                  autoComplete="new-password"
                  value={field.state.value}
                  onBlur={field.handleBlur}
                  onChange={(e) => field.handleChange(e.target.value)}
                />
                <FieldMessage errors={field.state.meta.errors} />
              </div>
            )}
          />

          <form.Subscribe
            selector={(state) => [state.canSubmit, state.isSubmitting]}
            children={([canSubmit, isSubmitting]) => (
              <TheButton
                type="submit"
                loading={isSubmitting}
                disabled={!canSubmit}
                className="w-full cursor-pointer"
              >
                Save
              </TheButton>
            )}
          />

          <form.Subscribe
            selector={(state) => state.errors}
            children={(errors) => <FormMessage errors={errors} />}
          />
        </form>
      </CardContent>
    </AuthLayout>
  );
}
